import { AnalyticsPlugin } from 'analytics';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

interface GoogleAnalyticsConfig {
  measurementId: string;
  debug?: boolean;
  customDimensions?: Record<string, string>;
  customMetrics?: Record<string, number>;
}

export const googleAnalyticsPlugin = (config: GoogleAnalyticsConfig): AnalyticsPlugin => {
  return {
    name: 'google-analytics',
    config: config,
    // Script loading is now handled by ConsentBasedGoogleAnalytics component
    // This plugin only handles event tracking
    initialize: () => {
      // No-op: Script loading handled by component in layout.tsx
    },
    
    page: ({ payload, config }: { payload: any; config: any }) => {
      const { measurementId } = config;

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('config', measurementId, {
          page_title: payload.title,
          page_location: typeof window !== 'undefined' ? window.location.href : undefined,
          page_path: payload.path,
        });

        window.gtag('event', 'page_view', {
          page_title: payload.title,
          page_location: typeof window !== 'undefined' ? window.location.href : undefined,
          page_path: payload.path,
        });
      }
    },

    track: ({ payload, config }: { payload: any; config: any }) => {
      if (typeof window !== 'undefined' && window.gtag) {
        const { event, properties = {} } = payload;

        // Convert analytics event names to GA4 format
        const gaEventName = convertEventName(event);

        window.gtag('event', gaEventName, {
          custom_parameter: event, // Keep original event name
          ...convertProperties(properties),
        });
      }
    },

    identify: ({ payload, config }: { payload: any; config: any }) => {
      if (typeof window !== 'undefined' && window.gtag) {
        const { userId, traits = {} } = payload;
        const { measurementId } = config;

        if (userId) {
          window.gtag('config', measurementId, {
            user_id: userId,
          });
        }

        // Set user properties
        if (Object.keys(traits).length > 0) {
          window.gtag('event', 'user_properties', convertProperties(traits));
        }
      }
    },

    reset: ({ config }: { config: any }) => {
      if (typeof window !== 'undefined' && window.gtag) {
        const { measurementId } = config;
        
        // Clear user identification
        window.gtag('config', measurementId, {
          user_id: null,
        });
      }
    },
  };
};

// Helper functions
function convertEventName(eventName: string): string {
  // Convert camelCase to snake_case for GA4 conventions
  return eventName
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '');
}

function convertProperties(properties: Record<string, any>): Record<string, any> {
  const converted: Record<string, any> = {};
  
  Object.entries(properties).forEach(([key, value]) => {
    // Convert property names to GA4 format
    const gaKey = key
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .replace(/^_/, '');
    
    // GA4 has restrictions on parameter values
    if (typeof value === 'string' && value.length > 100) {
      converted[gaKey] = value.substring(0, 100);
    } else if (typeof value === 'object' && value !== null) {
      converted[gaKey] = JSON.stringify(value);
    } else {
      converted[gaKey] = value;
    }
  });
  
  return converted;
}